import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { BEIGE_400, BEIGE_700, BEIGE_800, BOX_SHADOW_XL, GRAY_1000, GRAY_400, GRAY_600, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, ButtonBase, Stack, SxProps, Theme, Typography } from '@mui/material'

import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded'
import { EmbedCategoryHint } from '../embedCategoryHint'
import { EmbedCoBranding } from '../EmbedCoBranding.component'
import { FC } from 'react'
import Logo from 'components/common/Logo/Logo'
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded'
import { useEmbedCrm } from 'utils/embedSession/useEmbedCrm'
import { useTranslation } from 'react-i18next'
import valuePropDrone from 'assets/img/embed/value_prop_drone.webp'
import valuePropFloorPlans from 'assets/img/embed/value_prop_floor_plans.webp'
import valuePropMeasurements from 'assets/img/embed/value_prop_measurements.webp'
import valuePropPhotos from 'assets/img/embed/value_prop_photos.webp'
import valuePropVideos from 'assets/img/embed/value_prop_videos.webp'
import valuePropVirtualTours from 'assets/img/embed/value_prop_virtual_tours.webp'

/** Shared max width of the header copy and the card grid, as in the prototype */
const CONTENT_MAX_WIDTH = 1180

/**
 * Propstack's Medien tab gives the iframe roughly 560px of height on a laptop and 720px on a desktop.
 * Both card rows and the footer fit 960x560 with 12px copy and a 28px headline; from 1200x700 the copy
 * and the headline take the prototype's 14px and 32px and the gaps open up (ISSUE-3267, QA-351).
 */
const LARGE_FRAME = '@media (min-height: 700px) and (min-width: 1200px)'

/**
 * Card image frame: half of every viewport pixel beyond the 560px floor goes to the two image rows,
 * up to the prototype's 132px (reached from 680px of height).
 */
const CARD_IMAGE_SX: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
  height: 'clamp(7.2rem, 50vh - 20.8rem, 13.2rem)',
  mb: .8,
  borderRadius: '1.2rem',
  overflow: 'hidden',
  bgcolor: BEIGE_700,
}

/** Supporting copy (subtitle, card descriptions) */
const COPY_SX = { fontSize: '1.2rem', lineHeight: 1.4, [LARGE_FRAME]: { fontSize: '1.4rem', lineHeight: 1.5 } }

/**
 * The legacy stylesheet sizes every heading element (`html[bkbn-style="old"] h1 { font-size: 5rem }`)
 * and outranks a MUI variant class, which is what blew the headline up to 50px in production (QA-351).
 * `&&` doubles the class so the prototype's 32px (28px on small frames) wins.
 */
const TITLE_SX = {
  '&&': {
    fontSize: '2.8rem',
    lineHeight: '3.4rem',
    fontWeight: 550,
    letterSpacing: '-.015em',
    color: GRAY_900,
    [LARGE_FRAME]: { fontSize: '3.2rem', lineHeight: '4rem' },
  },
}

/** Class the card's hover/focus styles hook to invert the arrow button, as the prototype does */
const ARROW_CLASS = 'embed-value-proposition-arrow'

const CARD_SX: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  // ButtonBase centers its content; pin it to the top so cards in a row line up whatever their description length
  justifyContent: 'flex-start',
  p: '1rem 1rem 1.2rem',
  borderRadius: '1.6rem',
  bgcolor: WHITE,
  border: `1px solid ${BEIGE_700}`,
  boxShadow: '0px 2px 8px 0px rgba(19, 24, 29, 0.04)',
  textAlign: 'left',
  transition: 'border-color .15s ease, box-shadow .15s ease',
  '&:hover, &.Mui-focusVisible': { borderColor: BEIGE_800, boxShadow: BOX_SHADOW_XL },
  [`&:hover .${ARROW_CLASS}, &.Mui-focusVisible .${ARROW_CLASS}`]: { bgcolor: GRAY_1000, borderColor: GRAY_1000, color: WHITE },
}

const ARROW_SX: SxProps<Theme> = {
  flexShrink: 0,
  width: '4rem',
  height: '4rem',
  borderRadius: '50%',
  border: `1px solid ${GRAY_400}`,
  color: GRAY_900,
  transition: 'background-color .15s ease, border-color .15s ease, color .15s ease',
}

/** The six on-site services of the value-proposition overlay, in prototype order. */
const services: Array<{
  hint: EmbedCategoryHint
  labelKey: string
  descriptionKey: string
  img: string
  /** Prototype-tuned object-position for images whose subject is off-center */
  imgPosition?: string
  /** Video card carries a play affordance over its image, per the prototype */
  hasPlayOverlay?: boolean
}> = [
  { hint: EmbedCategoryHint.PHOTOS, labelKey: 'value_proposition.services.photos', descriptionKey: 'value_proposition.descriptions.photos', img: valuePropPhotos },
  { hint: EmbedCategoryHint.DRONE, labelKey: 'value_proposition.services.drone', descriptionKey: 'value_proposition.descriptions.drone', img: valuePropDrone },
  { hint: EmbedCategoryHint.VIDEOS, labelKey: 'value_proposition.services.videos', descriptionKey: 'value_proposition.descriptions.videos', img: valuePropVideos, hasPlayOverlay: true },
  { hint: EmbedCategoryHint.VIRTUAL_TOURS, labelKey: 'value_proposition.services.virtual_tours', descriptionKey: 'value_proposition.descriptions.virtual_tours', img: valuePropVirtualTours, imgPosition: 'center 32%' },
  { hint: EmbedCategoryHint.FLOOR_PLANS, labelKey: 'value_proposition.services.floor_plans', descriptionKey: 'value_proposition.descriptions.floor_plans', img: valuePropFloorPlans },
  { hint: EmbedCategoryHint.MEASUREMENTS, labelKey: 'value_proposition.services.measurements', descriptionKey: 'value_proposition.descriptions.measurements', img: valuePropMeasurements },
]

/**
 * @component
 * Value-proposition overlay shown on embed entry before the user's first order (ISSUE-2017), on the
 * prototype's cream ground: the BACKBONE wordmark with the prototype headline, six on-site service
 * cards (image, title, description, circular arrow) each deep-linking into the wizard pre-filtered
 * for that service, and a white footer bar with the BACKBONE × CRM co-branding and the
 * CRM→Backbone data-transfer notice. There is intentionally no single "Weiter" button — per
 * the prototype the cards are the only way forward.
 *
 * Presentation only: the caller owns what a picked service does, so the legacy
 * embed and PF v2 render this same component against their own seams rather than
 * keeping two copies of a layout tuned to Propstack's frame.
 *
 * @example <EmbedValueProposition onSelect={(hint) => { setCategoryHint(hint); dismiss() }} />
 */
interface Props {
  /** A service card was picked. Fires after the funnel event. */
  onSelect: (hint: EmbedCategoryHint) => void
}

export const EmbedValueProposition: FC<Props> = ({ onSelect }) => {
  const { t } = useTranslation(['embed'])
  const { crmName } = useEmbedCrm()

  const handleSelect = (hint: EmbedCategoryHint) => {
    logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_EMBED_OVERLAY_SERVICE_SELECTED, { service: hint })
    onSelect(hint)
  }

  return (
    <Stack height="100%" bgcolor={BEIGE_400}>

      {/* Auto margins (not justify-content) center the content so it stays reachable through the
          scrollbar when the frame is shorter than the overlay */}
      <Box
        flex="1 1 auto"
        minHeight={0}
        display="flex"
        flexDirection="column"
        sx={{ overflowY: 'auto', px: { xs: 1.6, md: 4 }, py: .8 }}
      >
        <Stack alignItems="center" gap={1} width="100%" maxWidth={CONTENT_MAX_WIDTH} sx={{ m: 'auto', [LARGE_FRAME]: { gap: 1.6 } }}>

          <Stack alignItems="center" gap={.6} sx={{ textAlign: 'center', [LARGE_FRAME]: { gap: 1 } }}>
            <Logo sx={{ display: 'flex', width: '24rem', mb: .2 }} />
            <Typography variant="h6" component="h1" sx={TITLE_SX}>{t('value_proposition.title')}</Typography>
            <Typography variant="text-sm" color={GRAY_700} maxWidth={640} sx={COPY_SX}>
              {t('value_proposition.subtitle')}
            </Typography>
          </Stack>

          <Box
            display="grid"
            width="100%"
            gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
            sx={{ gap: 1.2, [LARGE_FRAME]: { gap: 1.6 } }}
          >
            {services.map(({ hint, labelKey, descriptionKey, img, imgPosition, hasPlayOverlay }) => (
              <ButtonBase key={hint} onClick={() => handleSelect(hint)} sx={CARD_SX}>
                <Box sx={CARD_IMAGE_SX}>
                  <Box
                    component="img"
                    src={img}
                    alt=""
                    sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', objectPosition: imgPosition ?? 'center' }}
                  />
                  {hasPlayOverlay &&
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '4rem',
                        height: '4rem',
                        borderRadius: '50%',
                        bgcolor: 'rgba(255, 255, 255, .92)',
                        boxShadow: '0px 2px 10px 0px rgba(19, 24, 29, 0.18)',
                      }}
                    >
                      <PlayArrowRounded sx={{ fontSize: 20, color: GRAY_900 }} />
                    </Stack>
                  }
                </Box>
                <Typography variant="text-md" fontWeight="bold" color={GRAY_900} sx={{ px: .6, mb: .4 }}>
                  {t(labelKey)}
                </Typography>
                <Stack direction="row" alignItems="flex-end" justifyContent="space-between" gap={1.2} flex={1} sx={{ px: .6 }}>
                  <Typography
                    variant="text-sm"
                    color={GRAY_700}
                    sx={{ ...COPY_SX, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {t(descriptionKey)}
                  </Typography>
                  <Stack className={ARROW_CLASS} alignItems="center" justifyContent="center" sx={ARROW_SX}>
                    <ArrowForwardRounded sx={{ fontSize: 18 }} />
                  </Stack>
                </Stack>
              </ButtonBase>
            ))}
          </Box>

        </Stack>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        flexShrink={0}
        sx={{ px: { xs: 1.6, md: 4 }, py: .8, bgcolor: WHITE, borderTop: `1px solid ${GRAY_400}` }}
      >
        <EmbedCoBranding />
        <Typography variant="text-xs" color={GRAY_600} sx={{ flex: 1, minWidth: '24rem', textAlign: 'right', [LARGE_FRAME]: { fontSize: '1.4rem' } }}>
          {t('value_proposition.data_transfer_notice_crm', { crm: crmName })}
        </Typography>
      </Stack>

    </Stack>
  )
}
